import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from '../i18n';
import { searchClientsBasic, createBasicClient } from '../../../services/contractGeneratorService';
import './steps.css';

const StepClient = ({ DesignSystemComponents, formData, onChange }) => {
  const { Button, Card, ComboBox, Badge, Modal, Input, Toast } = DesignSystemComponents;
  const { user } = useAuth();
  const { t, language: cgLanguage } = useScopedTranslation(contractGenI18n);
  const [clientSearchText, setClientSearchText] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', identifier: '', email: '', telephone: '' });

  const selectedClient = formData?.cliente || null;
  const clientSelected = Boolean(selectedClient);
  const clientHasOtherContracts = false;
  const clientActive = selectedClient?.isActive ?? true;
  const canActiveClient = false;

  const fetchClients = async (text) => {
    if (!text || text.trim().length < 2) {
      setClientOptions([]);
      return;
    }

    setLoadingClients(true);
    try {
      const result = await searchClientsBasic({ text: text.trim() });
      setClientOptions(
        (result || []).map((c) => ({
          label: `${c.name} (${c.rut})`,
          value: c.clientId || c.id,
          id: c.clientId || c.id,
          name: c.name,
          rut: c.rut,
          email: c.email,
          phone: c.phone,
          isActive: c.active,
        }))
      );
    } catch (error) {
      console.error(error);
      setClientOptions([]);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => fetchClients(clientSearchText), 300);
    return () => clearTimeout(timeoutId);
  }, [clientSearchText]);

  const noOptionsMessage = useMemo(() => {
    if (!clientSearchText || clientSearchText.trim().length < 2) {
      return t('cg_client_min_chars');
    }
    return t('cg_client_no_results');
  }, [clientSearchText, cgLanguage]);

  const handleSaveNewClient = async () => {
    if (!newClient.name.trim() || !newClient.identifier.trim()) {
      Toast?.show?.({ title: t('cg_client_toast_missing_data'), message: t('cg_client_toast_name_rut_required'), variant: 'warning' });
      return;
    }
    setSavingClient(true);
    try {
      const result = await createBasicClient({
        name: newClient.name.trim(),
        identifier: newClient.identifier.trim(),
        email: newClient.email.trim() || null,
        telephone: newClient.telephone.trim() || null,
        creatorId: user?.userId || null,
      });
      const createdId = result?.id || result?.clientId;
      if (createdId) {
        const clientObj = {
          label: `${newClient.name.trim()} (${newClient.identifier.trim()})`,
          value: createdId,
          id: createdId,
          name: newClient.name.trim(),
          rut: newClient.identifier.trim(),
          email: newClient.email.trim(),
          phone: newClient.telephone.trim(),
          isActive: true,
        };
        onChange({ cliente: clientObj });
        Toast?.show?.({ title: t('cg_client_toast_created'), message: t('cg_client_toast_created_msg', { id: createdId }), variant: 'success' });
      }
      setShowNewClientModal(false);
      setNewClient({ name: '', identifier: '', email: '', telephone: '' });
    } catch (err) {
      console.error(err);
      Toast?.show?.({
        title: t('cg_client_toast_error'),
        message: err?.response?.data || err?.message || t('cg_client_toast_create_error'),
        variant: 'error',
      });
    } finally {
      setSavingClient(false);
    }
  };

  return (
    <div className='step-content mb-4'>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <p className='body fw-semibold'>{t('cg_client_select_client')}</p>
        <Button label={t('cg_client_new_client')} variant='naked' iconStart='plus' onClick={() => setShowNewClientModal(true)} />
      </div>

      <div className='row'>
        <div className='col-6'>
          <ComboBox
            label={t('cg_client_label')}
            menuPlacement='bottom'
            onChange={(value) => {
              const raw = Array.isArray(value) ? value[0] : value;
              // The Design System ComboBox strips custom props (value, id).
              // Match back to clientOptions by label to recover the full object.
              const rawLabel = raw?.label || (typeof raw === 'string' ? raw : '');
              const matched = raw && clientOptions.find(
                (o) => (rawLabel && o.label === rawLabel) || (raw.value && o.value === raw.value) || (raw.id && o.id === raw.id)
              );
              const fullClient = matched || raw;
              const clientId = fullClient?.value ?? fullClient?.id ?? 0;
              onChange({ cliente: fullClient, _clientId: Number(clientId) || 0 });
            }}
            onInputChange={(value) => setClientSearchText(value || '')}
            options={clientOptions}
            placeholder={t('cg_client_search_placeholder')}
            value={selectedClient}
            required
            isLoading={loadingClients}
            noOptionsMessage={() => noOptionsMessage}
          />
        </div>

        <div
          className={`col-6 d-flex flex-column ${!clientSelected && 'align-items-center justify-content-center'}`}
        >
          {clientSelected && !canActiveClient && !clientActive && (
            <div className='warning-message'>
              {t('cg_client_inactive_warning')}
            </div>
          )}
          <Card specialPadding='p-4'>
            {clientSelected ? (
              <div className='d-flex justify-content-between'>
                <div className='me-3'>
                  <p className='body mb-2'>
                    <span className='fw-bold'>{t('cg_client_name')}</span>
                    {selectedClient?.name || '--'}
                  </p>
                  <p className='body mb-2'>
                    <span className='fw-bold'>{t('cg_client_rut')}</span>
                    {selectedClient?.rut || '--'}
                  </p>
                  <p className='body mb-2'>
                    <span className='fw-bold'>{t('cg_client_email')}</span>
                    {selectedClient?.email || '--'}
                  </p>
                  <p className='body mb-2'>
                    <span className='fw-bold'>{t('cg_client_phone')}</span>
                    {selectedClient?.phone || '--'}
                  </p>
                  <p className='fw-bold'>{t('cg_client_other_contracts')}</p>
                  <ul className='list-of-cases'>
                    {clientHasOtherContracts ? (
                      <li>{t('cg_client_bl_service')}</li>
                    ) : (
                      <li>{t('cg_client_no_other_contracts')}</li>
                    )}
                  </ul>
                </div>
                <div>
                  {!canActiveClient && !clientActive && (
                    <Badge text={t('cg_client_badge_inactive')} color='light' size='sm' />
                  )}
                </div>
              </div>
            ) : (
              <p className='body'>{t('cg_client_select_to_see_info')}</p>
            )}
          </Card>
        </div>
      </div>
      {Modal && (
        <Modal
          onClose={() => setShowNewClientModal(false)}
          show={showNewClientModal}
          primaryBtnAction={handleSaveNewClient}
          primaryBtnTxt={savingClient ? t('cg_client_saving') : t('cg_client_create_btn')}
          disabledPrimaryBtn={savingClient || !newClient.name.trim() || !newClient.identifier.trim()}
          secondaryBtnAction={() => setShowNewClientModal(false)}
          secondaryBtnTxt={t('cg_btn_cancel')}
          title={t('cg_client_modal_title')}
          width='600px'
        >
          <div className='row'>
            <div className='col-6 mb-3'>
              <Input
                label={t('cg_client_full_name')}
                value={newClient.name}
                onChange={(e) => setNewClient((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('cg_client_full_name_placeholder')}
                required
              />
            </div>
            <div className='col-6 mb-3'>
              <Input
                label={t('cg_client_rut_label')}
                value={newClient.identifier}
                onChange={(e) => setNewClient((prev) => ({ ...prev, identifier: e.target.value }))}
                placeholder={t('cg_client_rut_placeholder')}
                required
              />
            </div>
            <div className='col-6 mb-3'>
              <Input
                label={t('cg_client_email_label')}
                type='email'
                value={newClient.email}
                onChange={(e) => setNewClient((prev) => ({ ...prev, email: e.target.value }))}
                placeholder={t('cg_client_email_placeholder')}
              />
            </div>
            <div className='col-6 mb-3'>
              <Input
                label={t('cg_client_phone_label')}
                value={newClient.telephone}
                onChange={(e) => setNewClient((prev) => ({ ...prev, telephone: e.target.value }))}
                placeholder={t('cg_client_phone_placeholder')}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StepClient;
